import { QueryClientProvider } from '@tanstack/react-query';
import React, { lazy } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { RouteShell } from '@/components/layout/RouteShell';
import { ScrollToTop } from '@/components/layout/ScrollToTop';
import { CookieConsent } from '@/components/site/CookieConsent';
import { InstallPWA } from '@/components/site/InstallPWA';
import { PopupBanner } from '@/components/site/PopupBanner';
import { BlogPostSkeleton } from '@/components/ui/BlogPostSkeleton';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { HeroSkeleton } from '@/components/ui/HeroSkeleton';
import { ToastProvider } from '@/components/ui/Toast';
import { EVENTS_PAGE_ENABLED } from '@/config/site';
import { CATEGORIES } from '@/lib/categories';
import { queryClient } from '@/lib/queryClient';
import { HomePage } from '@/pages/HomePage';

// Every page except the home page is code-split
const AuthorPage = lazy(() =>
  import('@/pages/AuthorPage').then((m) => ({ default: m.AuthorPage }))
);
const BlogListPage = lazy(() =>
  import('@/pages/BlogListPage').then((m) => ({ default: m.BlogListPage }))
);
const BlogPostPage = lazy(() =>
  import('@/pages/BlogPostPage').then((m) => ({ default: m.BlogPostPage }))
);
const CategoryPage = lazy(() =>
  import('@/pages/CategoryPage').then((m) => ({ default: m.CategoryPage }))
);
const ContactPage = lazy(() =>
  import('@/pages/ContactPage').then((m) => ({ default: m.ContactPage }))
);
const EventsPage = lazy(() =>
  import('@/pages/EventsPage').then((m) => ({ default: m.EventsPage }))
);
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);
const SearchPage = lazy(() =>
  import('@/pages/SearchPage').then((m) => ({ default: m.SearchPage }))
);

interface AppRoute {
  path: string;
  element: React.ReactNode;
  /** Loading placeholder while the page's code downloads (default: spinner) */
  fallback?: React.ReactNode;
}

const ROUTES: AppRoute[] = [
  { path: '/', element: <HomePage /> },
  ...CATEGORIES.map((category) => ({
    path: category.path,
    element: <CategoryPage key={category.slug} category={category} />,
    fallback: <HeroSkeleton />,
  })),
  { path: '/blog', element: <BlogListPage /> },
  { path: '/blog/:slug', element: <BlogPostPage />, fallback: <BlogPostSkeleton /> },
  { path: '/kereses', element: <SearchPage /> },
  { path: '/author/:authorSlug', element: <AuthorPage /> },
  {
    path: '/programajanlo',
    element: EVENTS_PAGE_ENABLED ? <EventsPage /> : <Navigate to="/" replace />,
    fallback: <HeroSkeleton />,
  },
  { path: '/kapcsolat', element: <ContactPage />, fallback: <HeroSkeleton /> },
  { path: '*', element: <NotFoundPage /> },
];

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <BrowserRouter>
            <ToastProvider>
              <ScrollToTop />
              <PopupBanner />
              <InstallPWA />
              <CookieConsent />
              <Routes>
                {ROUTES.map(({ path, element, fallback }) => (
                  <Route
                    key={path}
                    path={path}
                    element={<RouteShell fallback={fallback}>{element}</RouteShell>}
                  />
                ))}
              </Routes>
            </ToastProvider>
          </BrowserRouter>
        </HelmetProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
